import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { User, Wallet } from '@nimbazaar/types';
import type { RootState } from '../store';
import { authApi } from '../api/authApi';

interface AuthState {
  user: User | null;
  wallet: Wallet | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Verification state
  pendingVerification: {
    email: string;
    name: string;
  } | null;
}

// Load initial state from localStorage
const loadAuthState = (): Partial<AuthState> => {
  try {
    const storedAuth = localStorage.getItem('auth');
    if (storedAuth) {
      const parsed = JSON.parse(storedAuth);
      return {
        user: parsed.user || null,
        wallet: parsed.wallet || null,
        accessToken: parsed.accessToken || null,
        refreshToken: parsed.refreshToken || null,
        isAuthenticated: !!parsed.accessToken,
      };
    }
  } catch {
    // Ignore errors
  }
  return {};
};

const persistedState = loadAuthState();

const initialState: AuthState = {
  user: persistedState.user || null,
  wallet: persistedState.wallet || null,
  accessToken: persistedState.accessToken || null,
  refreshToken: persistedState.refreshToken || null,
  isAuthenticated: persistedState.isAuthenticated || false,
  isLoading: false,
  error: null,
  pendingVerification: null,
};

// Helper to save auth state to localStorage
const saveAuthState = (state: AuthState) => {
  try {
    localStorage.setItem('auth', JSON.stringify({
      user: state.user,
      wallet: state.wallet,
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
    }));
  } catch {
    // Ignore errors
  }
};

// Helper to clear auth state from localStorage
const clearAuthState = () => {
  try {
    localStorage.removeItem('auth');
  } catch {
    // Ignore errors
  }
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        wallet?: Wallet | null;
        accessToken: string;
        refreshToken: string;
      }>
    ) => {
      state.user = action.payload.user;
      state.wallet = action.payload.wallet || null;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      state.pendingVerification = null;
      saveAuthState(state);
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      saveAuthState(state);
    },
    setWallet: (state, action: PayloadAction<Wallet>) => {
      state.wallet = action.payload;
      saveAuthState(state);
    },
    setPendingVerification: (
      state,
      action: PayloadAction<{ email: string; name: string }>
    ) => {
      state.pendingVerification = action.payload;
    },
    clearPendingVerification: (state) => {
      state.pendingVerification = null;
    },
    setTokens: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      saveAuthState(state);
    },
    logout: (state) => {
      state.user = null;
      state.wallet = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.pendingVerification = null;
      clearAuthState();
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addMatcher(authApi.endpoints.login.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.data.user;
        state.wallet = action.payload.data.wallet;
        state.accessToken = action.payload.data.accessToken;
        state.refreshToken = action.payload.data.refreshToken;
        state.isAuthenticated = true;
        state.pendingVerification = null;
        saveAuthState(state);
      })
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        state.isLoading = false;
        // Extract error message from API response
        const error = action.payload as { data?: { error?: { message?: string } } } | undefined;
        state.error = error?.data?.error?.message || action.error.message || 'Login failed';
      })

      // Register - doesn't authenticate, just sets pending verification
      .addMatcher(authApi.endpoints.register.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.register.matchFulfilled, (state, action) => {
        state.isLoading = false;
        // Set pending verification - user needs to verify OTP
        state.pendingVerification = {
          email: action.payload.data.user.email,
          name: action.payload.data.user.name,
        };
      })
      .addMatcher(authApi.endpoints.register.matchRejected, (state, action) => {
        state.isLoading = false;
        const error = action.payload as { data?: { error?: { message?: string } } } | undefined;
        state.error = error?.data?.error?.message || action.error.message || 'Registration failed';
      })

      // Verify OTP - authenticates user
      .addMatcher(authApi.endpoints.verifyOtp.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.verifyOtp.matchFulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.data.user;
        state.wallet = action.payload.data.wallet;
        state.accessToken = action.payload.data.accessToken;
        state.refreshToken = action.payload.data.refreshToken;
        state.isAuthenticated = true;
        state.pendingVerification = null;
        saveAuthState(state);
      })
      .addMatcher(authApi.endpoints.verifyOtp.matchRejected, (state, action) => {
        state.isLoading = false;
        const error = action.payload as { data?: { error?: { message?: string } } } | undefined;
        state.error = error?.data?.error?.message || action.error.message || 'OTP verification failed';
      })

      // Google Auth
      .addMatcher(authApi.endpoints.googleAuth.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.googleAuth.matchFulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.data.user;
        state.wallet = action.payload.data.wallet;
        state.accessToken = action.payload.data.accessToken;
        state.refreshToken = action.payload.data.refreshToken;
        state.isAuthenticated = true;
        state.pendingVerification = null;
        saveAuthState(state);
      })
      .addMatcher(authApi.endpoints.googleAuth.matchRejected, (state, action) => {
        state.isLoading = false;
        const error = action.payload as { data?: { error?: { message?: string } } } | undefined;
        state.error = error?.data?.error?.message || action.error.message || 'Google authentication failed';
      })

      // Logout
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.user = null;
        state.wallet = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.pendingVerification = null;
        clearAuthState();
      })

      // Refresh token
      .addMatcher(authApi.endpoints.refreshToken.matchFulfilled, (state, action) => {
        state.accessToken = action.payload.data.accessToken;
        state.refreshToken = action.payload.data.refreshToken;
        saveAuthState(state);
      })

      // Get me
      .addMatcher(authApi.endpoints.getMe.matchFulfilled, (state, action) => {
        state.user = action.payload.data.user;
        state.wallet = action.payload.data.wallet as Wallet;
        saveAuthState(state);
      })

      // Update profile
      .addMatcher(authApi.endpoints.updateProfile.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.updateProfile.matchFulfilled, (state, action) => {
        state.isLoading = false;
        // Handle different possible response structures
        const payload = action.payload as { data?: { user?: User }; user?: User };
        const updatedUser = payload.data?.user || payload.user;

        if (updatedUser && state.user) {
          // Merge updated user data with existing user to preserve fields not returned by API
          state.user = { ...state.user, ...updatedUser } as User;
        } else if (updatedUser) {
          state.user = updatedUser;
        }
        saveAuthState(state);
      })
      .addMatcher(authApi.endpoints.updateProfile.matchRejected, (state, action) => {
        state.isLoading = false;
        const error = action.payload as { data?: { error?: { message?: string } } } | undefined;
        state.error = error?.data?.error?.message || action.error.message || 'Failed to update profile';
      });
  },
});

export const authActions = authSlice.actions;

// Selectors
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectWallet = (state: RootState) => state.auth.wallet;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;
export const selectRefreshToken = (state: RootState) => state.auth.refreshToken;
export const selectPendingVerification = (state: RootState) => state.auth.pendingVerification;
